import { Modal } from "bootstrap";

const modalState = new Map();
const getActiveModals = () => Array.from(document.querySelectorAll(".modal.show"));

const lockBodyScroll = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add("modal-open");
};

const unlockBodyScroll = () => {
    const activeModals = getActiveModals();
    if (activeModals.length === 0) {
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    }
};

const updateModalZIndices = () => {
    const activeModals = getActiveModals();
    activeModals.forEach((element, index) => {
        const stackLevel = index + 1;
        element.style.zIndex = String(1050 + stackLevel * 10);

        const state = modalState.get(element);
        if (state?.backdropElement) {
            state.backdropElement.style.zIndex = String(1040 + stackLevel * 10);
            state.stackLevel = stackLevel;
        } else if (state) {
            state.stackLevel = stackLevel;
        }
    });

    if (activeModals.length > 0) {
        lockBodyScroll();
    } else {
        unlockBodyScroll();
    }
};

const createCustomBackdrop = (modalElement, stackLevel) => {
    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop fade show stacked-backdrop";
    backdrop.style.zIndex = String(1040 + stackLevel * 10);

    backdrop.addEventListener("mousedown", (event) => {
        event.preventDefault();
        const instance = Modal.getInstance(modalElement);
        instance?.hide();
    });

    document.body.appendChild(backdrop);
    return backdrop;
};

const focusTopActiveModal = () => {
    const activeModals = Array.from(document.querySelectorAll(".modal.show"));
    if (activeModals.length === 0) return;

    const topModal = activeModals[activeModals.length - 1];

    if (!topModal.hasAttribute("tabindex")) {
        topModal.setAttribute("tabindex", "-1");
    }

    setTimeout(() => topModal.focus(), 0);
};

const registerModalHiddenHandler = (modalElement) => {
    const state = modalState.get(modalElement);
    if (state?.hiddenHandlerBound) return;

    const onHidden = () => {
        const currentState = modalState.get(modalElement);

        if (currentState?.backdropElement) {
            currentState.backdropElement.remove();
        }

        if (currentState?.outsideClickHandler) {
            modalElement.removeEventListener("mousedown", currentState.outsideClickHandler);
        }

        modalState.delete(modalElement);

        updateModalZIndices();
        focusTopActiveModal();
    };

    modalElement.addEventListener("hidden.bs.modal", onHidden);

    modalState.set(modalElement, {
        ...(state || {}),
        hiddenHandlerBound: onHidden,
    });
};

const attachOutsideClickHandler = (modalElement) => {
    const handler = (event) => {
        if (event.target === modalElement) {
            const instance = Modal.getInstance(modalElement);
            instance?.hide();
        }
    };

    modalElement.addEventListener("mousedown", handler);
    return handler;
};

const showModal = (modalElement) => {
    if (!modalElement) return;

    registerModalHiddenHandler(modalElement);

    const stackLevel = getActiveModals().length + 1;

    const previousState = modalState.get(modalElement) || {};
    modalState.set(modalElement, { ...previousState, stackLevel });

    modalElement.style.zIndex = String(1050 + stackLevel * 10);

    const existingInstance = Modal.getInstance(modalElement);
    if (existingInstance) {
        existingInstance.dispose();
    }

    const modalInstance = new Modal(modalElement, {
        backdrop: false,
        keyboard: true,
        focus: true
    });

    const backdropElement = createCustomBackdrop(modalElement, stackLevel);
    const outsideClickHandler = attachOutsideClickHandler(modalElement);

    const state = modalState.get(modalElement);
    modalState.set(modalElement, {
        ...state,
        backdropElement,
        outsideClickHandler,
        stackLevel
    });

    lockBodyScroll();

    modalInstance.show();

    setTimeout(() => {
        updateModalZIndices();
        focusTopActiveModal();
    }, 0);
};

const hideModal = (modalElement) => {
    if (!modalElement) return;

    const modalInstance = Modal.getInstance(modalElement);
    if (!modalInstance) return;

    modalInstance.hide();
};

const removeModalBackdrop = () => {
    const activeModals = Array.from(document.querySelectorAll(".modal.show"));

    activeModals.forEach((modalElement) => {
        try {
            const instance = Modal.getInstance(modalElement);
            if (instance) {
                instance.hide();
            } else {
                modalElement.classList.remove("show");
                modalElement.style.display = "none";
                modalElement.setAttribute("aria-hidden", "true");
                modalElement.removeAttribute("aria-modal");
            }
        } catch (error) {
            modalElement.classList.remove("show");
            modalElement.style.display = "none";
            modalElement.setAttribute("aria-hidden", "true");
            modalElement.removeAttribute("aria-modal");
        }
    });

    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");

    document.querySelectorAll(".modal-scrollbar-measure").forEach((element) => element.remove());
};

export { removeModalBackdrop, showModal, hideModal };